import { useState, type ReactNode } from "react";
import { EditorContent, useEditor, useEditorState, type Editor, type JSONContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TableKit } from "@tiptap/extension-table";
import { Placeholder } from "@tiptap/extensions";
import { Bold, Italic, Link2, List, ListOrdered, Minus, Redo2, Table as TableIcon, Underline, Undo2 } from "lucide-react";
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@lumanity/ui/primitives";
import { cn } from "@/lib/utils";
import { initialContent } from "./editorContent";
import { countWords } from "./outlineModel";

const extensions = (placeholder?: string) => [
  StarterKit.configure({ heading: { levels: [3, 4] }, link: { openOnClick: false, autolink: true } }),
  TableKit.configure({ table: { resizable: false } }),
  ...(placeholder ? [Placeholder.configure({ placeholder })] : []),
];

const ToolButton = ({ label, active, disabled, onClick, children }: { label: string; active?: boolean; disabled?: boolean; onClick: () => void; children: ReactNode }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    aria-pressed={active}
    disabled={disabled}
    onMouseDown={(e) => e.preventDefault()} // keep the selection in the editor
    onClick={onClick}
    className={cn("rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40", active && "bg-muted text-foreground")}
  >
    {children}
  </button>
);

const Divider = () => <span className="mx-1 h-5 w-px bg-border" aria-hidden />;

type BlockType = "paragraph" | "h3" | "h4";

const Toolbar = ({ editor }: { editor: Editor }) => {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"),
      italic: e.isActive("italic"),
      underline: e.isActive("underline"),
      link: e.isActive("link"),
      bullet: e.isActive("bulletList"),
      ordered: e.isActive("orderedList"),
      block: (e.isActive("heading", { level: 3 }) ? "h3" : e.isActive("heading", { level: 4 }) ? "h4" : "paragraph") as BlockType,
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  });
  const chain = () => editor.chain().focus();
  const setBlock = (value: BlockType) => {
    if (value === "paragraph") chain().setParagraph().run();
    else chain().setHeading({ level: value === "h3" ? 3 : 4 }).run();
  };
  const toggleLink = () => {
    if (state.link) {
      chain().unsetLink().run();
      return;
    }
    const href = window.prompt("Link address");
    if (href) chain().extendMarkRange("link").setLink({ href }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b px-2 py-1" role="toolbar" aria-label="Formatting">
      <ToolButton label="Undo" disabled={!state.canUndo} onClick={() => chain().undo().run()}>
        <Undo2 className="h-4 w-4" />
      </ToolButton>
      <ToolButton label="Redo" disabled={!state.canRedo} onClick={() => chain().redo().run()}>
        <Redo2 className="h-4 w-4" />
      </ToolButton>
      <Divider />
      <ToolButton label="Bold" active={state.bold} onClick={() => chain().toggleBold().run()}>
        <Bold className="h-4 w-4" />
      </ToolButton>
      <ToolButton label="Italic" active={state.italic} onClick={() => chain().toggleItalic().run()}>
        <Italic className="h-4 w-4" />
      </ToolButton>
      <ToolButton label="Underline" active={state.underline} onClick={() => chain().toggleUnderline().run()}>
        <Underline className="h-4 w-4" />
      </ToolButton>
      <Divider />
      <Select value={state.block} onValueChange={(v) => setBlock(v as BlockType)}>
        <SelectTrigger className="h-7 w-32 text-xs" aria-label="Text style">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="paragraph">Paragraph</SelectItem>
          <SelectItem value="h3">Heading</SelectItem>
          <SelectItem value="h4">Subheading</SelectItem>
        </SelectContent>
      </Select>
      <Divider />
      <ToolButton label={state.link ? "Remove link" : "Link"} active={state.link} onClick={toggleLink}>
        <Link2 className="h-4 w-4" />
      </ToolButton>
      <ToolButton label="Insert table" onClick={() => chain().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
        <TableIcon className="h-4 w-4" />
      </ToolButton>
      <ToolButton label="Horizontal line" onClick={() => chain().setHorizontalRule().run()}>
        <Minus className="h-4 w-4" />
      </ToolButton>
      <Divider />
      <ToolButton label="Bulleted list" active={state.bullet} onClick={() => chain().toggleBulletList().run()}>
        <List className="h-4 w-4" />
      </ToolButton>
      <ToolButton label="Numbered list" active={state.ordered} onClick={() => chain().toggleOrderedList().run()}>
        <ListOrdered className="h-4 w-4" />
      </ToolButton>
    </div>
  );
};

interface SectionEditorProps {
  json: JSONContent | null;
  text: string;
  wordLimit: number | null;
  saving: boolean;
  onSave: (json: JSONContent, text: string) => void;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

/** Rich-text editing of one section, saved explicitly. */
export const SectionEditor = ({ json, text, wordLimit, saving, onSave, onCancel, onDirtyChange }: SectionEditorProps) => {
  const [words, setWords] = useState(() => countWords(text));
  const editor = useEditor({
    extensions: extensions("Write this section…"),
    content: initialContent(json, text),
    autofocus: "end",
    immediatelyRender: true,
    onUpdate: ({ editor: e }) => {
      setWords(countWords(e.getText()));
      onDirtyChange?.(true);
    },
  });

  if (!editor) return null;
  const over = wordLimit !== null && words > wordLimit;
  return (
    <div>
      <div className="rounded-md border bg-background">
        <Toolbar editor={editor} />
        <EditorContent editor={editor} className="section-content prose prose-sm max-w-none px-4 py-3" />
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button type="button" size="sm" variant="brand" disabled={saving} onClick={() => onSave(editor.getJSON(), editor.getText({ blockSeparator: "\n" }))}>
          {saving ? "Saving..." : "Save"}
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
        <span className={cn("ml-auto text-xs tabular-nums", over ? "text-red-700" : "text-muted-foreground")}>
          {words}
          {wordLimit !== null ? ` / ${wordLimit}` : ""} words
        </span>
      </div>
    </div>
  );
};

/** A section's saved text, read-only. */
export const SectionContent = ({ json, text }: { json: JSONContent | null; text: string }) => {
  const editor = useEditor(
    { extensions: extensions(), content: initialContent(json, text), editable: false, immediatelyRender: true },
    [json, text],
  );
  return <EditorContent editor={editor} className="section-content prose prose-sm max-w-none [&_.ProseMirror]:min-h-0" />;
};
